import { ChangeDetectionStrategy, Component, computed, OnInit, signal, inject } from '@angular/core';
import {LettersService} from '../../services/letters.service';
import {MemberStateService} from '../../services/member-state.service';
import {FileService} from '../../services/file.service';
import {FormatService} from '../../services/format.service';
import {UserContextService} from '../../services/user-context.service';
import {UnemploymentApplicationService} from '../../services/unemployment-application.service';
import {catchError, finalize, map, mergeMap, startWith, switchMap, take, tap} from 'rxjs/operators';
import {EMPTY, forkJoin, Observable, Subject} from 'rxjs';
import {format, startOfYear, startOfDay, subYears, parse, isAfter, isEqual} from 'date-fns';
import {StorageService} from '../../services/storage.service';
import {DatePickerOptions} from '../../model/date-picker-options';
import {Member} from '../../model/member';
import {UnemploymentPaymentRequest} from '../../model/unemployment-payment-request';
import {LetterResponse} from '../../model/letter-response';
import {UnemploymentApplicationStatus} from '../../model/unemployment-application-status';
import {ApplicationResponse} from '../../model/application-response';
import {LocalizedText} from '../../model/localized-text';
import {ViewState, toViewState} from '../../model/view-state';
import { UnemploymentEndDateComponent } from '../shared/unemployment-end-date/unemployment-end-date.component';
import { IntroductionComponent } from '../shared/introduction/introduction.component';
import { FilterComponent } from '../shared/filter/filter.component';
import { DateFilterComponent } from '../shared/filter/date-filter/date-filter.component';
import { AlertComponent } from '../shared/alert/alert.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { AsyncPipe, NgClass, DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { IncompleteRequestInfoComponent } from '../shared/incomplete-request-info/incomplete-request-info.component';

interface RequestsData {
  paymentRequests: UnemploymentPaymentRequest[];
  unemploymentApplications: ApplicationResponse[];
}

@Component({
    selector: 'myabvv-requests',
    standalone: true,
    templateUrl: './requests.component.html',
    styleUrls: ['./requests.component.css'],
    imports: [
      UnemploymentEndDateComponent, IntroductionComponent, FilterComponent, DateFilterComponent,
      AlertComponent, LoaderComponent, AsyncPipe, NgClass, TranslatePipe, PaginationComponent,
      DatePipe, IncompleteRequestInfoComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestsComponent implements OnInit {
  private memberStateService = inject(MemberStateService);
  private fileService = inject(FileService);
  private formatService = inject(FormatService);
  private lettersService = inject(LettersService);
  private storage = inject(StorageService);
  private userContext = inject(UserContextService);
  private unemploymentApplicationService = inject(UnemploymentApplicationService);


  state$: Observable<ViewState<RequestsData>>;
  isDownloading = signal(false);
  hasDownloadError = signal(false);
  startDate: Date;
  endDate: Date;
  datePickerOptionsStartDate: DatePickerOptions;
  datePickerOptionsEndDate: DatePickerOptions;
  calendarMaxEndDate = new Date();

  // Pagination
  rowsPerPage = 5;
  currentPage = signal(1);
  paymentRequests = signal<UnemploymentPaymentRequest[]>([]);
  pages = computed(() => {
    const totalPages = Math.ceil(this.paymentRequests().length / this.rowsPerPage);
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  });
  pagedRequests = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.rowsPerPage;
    return this.paymentRequests().slice(startIndex, startIndex + this.rowsPerPage);
  });

  private fetchTrigger$ = new Subject<void>();

  ngOnInit() {
    this.initializeDates();
    this.initializeDatePickerOptionsStartDate();
    this.initializeDatePickerOptionsEndDate();
    this.state$ = this.fetchTrigger$.pipe(
      startWith(undefined),
      switchMap(() => this.buildFetchSource$().pipe(toViewState()))
    );
  }

  downloadRequest(paymentRequest: UnemploymentPaymentRequest) {
    this.isDownloading.set(true);
    this.hasDownloadError.set(false);
    const isInternalUser = this.userContext.isInternalUser();

    // Use /me endpoint for external users, /{inss} for internal users
    const download$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          switchMap((member: Member) => this.lettersService.getPaymentRequests(
            member.personalInformation.inss,
            paymentRequest.creationDate,
            paymentRequest.docNumber,
            paymentRequest.docRegion
          ))
        )
      : this.lettersService.getMyPaymentRequests(
          paymentRequest.creationDate,
          paymentRequest.docNumber,
          paymentRequest.docRegion
        );

    download$.pipe(
      tap((res: LetterResponse) => {
        this.fileService.downloadFile(res);
      }),
      catchError(() => {
        this.hasDownloadError.set(true);
        return EMPTY;
      }),
      finalize(() => {
        this.isDownloading.set(false);
      })
    ).subscribe();
  }

  onApplyFilter() {
    this.storeDates();
    this.fetchTrigger$.next();
  }

  onReinitializeFilters() {
    this.initializeDates();
    this.onApplyFilter();
  }

  onChangeStartDate(newDate: Date) {
    this.startDate = newDate;
  }

  onChangeEndDate(newDate: Date) {
    this.endDate = newDate;
  }

  storeDates() {
    this.storage.setStartDate(this.startDate.toString());
    this.storage.setEndDate(this.endDate.toString());
  }

  getTranslatedData(array: LocalizedText[]): string {
    return this.formatService.getTranslatedData(array);
  }

  getRequestType(paymentRequest: UnemploymentPaymentRequest): string {
    return this.formatService.getRequestType(paymentRequest);
  }

  getUnemploymentApplication(
    applications: ApplicationResponse[],
    targetDate: string,
    requestState: string,
    requestSequence?: number
  ): ApplicationResponse | null {
    return this.unemploymentApplicationService.findMatchingApplication(
      applications, targetDate, requestState, requestSequence
    );
  }

  hasMissingDocuments(application: ApplicationResponse): boolean {
    return !!this.unemploymentApplicationService.extractMissingDocuments(application);
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
  }

  getStatusBadgeClass(requestState: UnemploymentApplicationStatus): string {
    switch (requestState) {
      case UnemploymentApplicationStatus.COMPLETED:
        return 'c-status-badge--success';
      case UnemploymentApplicationStatus.IMPOSSIBLE:
        return 'c-status-badge--error';
      case UnemploymentApplicationStatus.CLOSED:
        return 'c-status-badge--neutral';
      case UnemploymentApplicationStatus.TO_BE_TREATED:
      case UnemploymentApplicationStatus.INCOMPLETE:
      case UnemploymentApplicationStatus.SUBMISSION:
      case UnemploymentApplicationStatus.EXTEND_DEADLINE:
      case UnemploymentApplicationStatus.EXPECTED_EXTENDED_DEADLINE:
      default:
        return 'c-status-badge--warning';
    }
  }

  getStatusIcon(requestState: UnemploymentApplicationStatus): string {
    switch (requestState) {
      case UnemploymentApplicationStatus.COMPLETED:
        return 'fa-check';
      case UnemploymentApplicationStatus.IMPOSSIBLE:
        return 'fa-times';
      case UnemploymentApplicationStatus.CLOSED:
        return 'fa-minus';
      case UnemploymentApplicationStatus.TO_BE_TREATED:
      case UnemploymentApplicationStatus.INCOMPLETE:
      case UnemploymentApplicationStatus.SUBMISSION:
      case UnemploymentApplicationStatus.EXTEND_DEADLINE:
      case UnemploymentApplicationStatus.EXPECTED_EXTENDED_DEADLINE:
      default:
        return 'fa-clock';
    }
  }

  getStatusDotClass(requestState: UnemploymentApplicationStatus): string {
    switch (requestState) {
      case UnemploymentApplicationStatus.COMPLETED:
        return 'timeline-dot--success';
      case UnemploymentApplicationStatus.IMPOSSIBLE:
        return 'timeline-dot--error';
      case UnemploymentApplicationStatus.CLOSED:
        return 'timeline-dot--neutral';
      case UnemploymentApplicationStatus.TO_BE_TREATED:
      case UnemploymentApplicationStatus.INCOMPLETE:
      case UnemploymentApplicationStatus.SUBMISSION:
      case UnemploymentApplicationStatus.EXTEND_DEADLINE:
      case UnemploymentApplicationStatus.EXPECTED_EXTENDED_DEADLINE:
      default:
        return 'timeline-dot--warning';
    }
  }

  private initializeDates() {
    this.startDate = subYears(startOfYear(new Date()), 1);
    this.endDate = startOfDay(new Date());
  }

  private initializeDatePickerOptionsStartDate() {
    this.datePickerOptionsStartDate = {
      format: 'dd-mm-yy',
      endDate: this.calendarMaxEndDate, // the MAX date that can be selected
      startView: 'date'
    };
  }

  private initializeDatePickerOptionsEndDate() {
    this.datePickerOptionsEndDate = {
      format: 'dd-mm-yy',
      endDate: this.calendarMaxEndDate, // the MAX date that can be selected
      startView: 'date'
    };
  }

  private buildFetchSource$(): Observable<RequestsData> {
    const isInternalUser = this.userContext.isInternalUser();

    const startDateStr = format(this.startDate, 'yyyy-MM-dd');
    const endDateStr = format(this.endDate, 'yyyy-MM-dd');
    const cutoffDate = parse('2025-05-01', 'yyyy-MM-dd', new Date());
    const statuses = [
      UnemploymentApplicationStatus.INCOMPLETE,
      UnemploymentApplicationStatus.EXTEND_DEADLINE,
      UnemploymentApplicationStatus.EXPECTED_EXTENDED_DEADLINE
    ];

    // Use /me endpoints for external users, /{inss} for internal users
    const fetchData$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          mergeMap((member: Member) => {
            const inss = member.personalInformation.inss;
            return forkJoin({
              paymentRequests: this.lettersService.getPaymentRequestsOverview(inss, startDateStr, endDateStr),
              unemploymentApplications: this.lettersService.getAllUnemploymentApplications(inss, statuses)
            });
          })
        )
      : forkJoin({
          paymentRequests: this.lettersService.getMyPaymentRequestsOverview(startDateStr, endDateStr),
          unemploymentApplications: this.lettersService.getMyAllUnemploymentApplications(statuses)
        });

    return fetchData$.pipe(
      map(({ paymentRequests, unemploymentApplications }) => {
        const filteredApplications = unemploymentApplications.filter(app => {
          const appDate = parse(app.requestDate, 'yyyy-MM-dd', new Date());
          return isAfter(appDate, cutoffDate) || isEqual(appDate, cutoffDate);
        });
        this.currentPage.set(1);
        this.paymentRequests.set(paymentRequests);
        return { paymentRequests, unemploymentApplications: filteredApplications };
      })
    );
  }
}
